import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ResBodyType } from 'otimus-shared'
import { Observable, map } from 'rxjs'
import { AuthService } from '../../../services/auth.service'
import { ServerUrlService } from '../../../services/server-url.service'
import {
  ChatEntity,
  ChatMessageEntity,
  GiphyMediaType,
  GiphySearchResult,
  UserSearchResult,
} from '../interfaces/chat.interface'

@Injectable({ providedIn: 'root' })
export class MessageChatService {
  constructor(
    private readonly http: HttpClient,
    private readonly serverUrl: ServerUrlService,
    private readonly authService: AuthService
  ) {}

  private get baseUrl(): string {
    return `${this.serverUrl.url}/chat`
  }

  private get headers() {
    return { Authorization: `Bearer ${this.authService.token}` }
  }

  findChats(): Observable<ChatEntity[]> {
    return this.http
      .get<ResBodyType<ChatEntity[]>>(this.baseUrl, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  findChatById(id: number): Observable<ChatEntity> {
    return this.http
      .get<ResBodyType<ChatEntity>>(`${this.baseUrl}/${id}`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data))
  }

  createChat(
    name: string,
    type: 'GROUP' | 'PRIVATE',
    participantIds: number[]
  ): Observable<ChatEntity> {
    return this.http
      .post<
        ResBodyType<ChatEntity>
      >(this.baseUrl, { name, type, participantIds }, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  updateChat(chatId: number, name: string): Observable<ChatEntity> {
    return this.http
      .put<
        ResBodyType<ChatEntity>
      >(`${this.baseUrl}/${chatId}`, { name }, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  findMessages(chatId: number, limit = 50, beforeId?: number): Observable<ChatMessageEntity[]> {
    const params: Record<string, string> = { limit: String(limit) }
    if (beforeId) {
      params['beforeId'] = String(beforeId)
    }

    return this.http
      .get<
        ResBodyType<ChatMessageEntity[]>
      >(`${this.baseUrl}/${chatId}/message`, { headers: this.headers, params })
      .pipe(map((res) => res.data))
  }

  sendMessage(chatId: number, content?: string): Observable<ChatMessageEntity> {
    return this.http
      .post<
        ResBodyType<ChatMessageEntity>
      >(`${this.baseUrl}/${chatId}/message`, { content }, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  editMessage(chatId: number, messageId: number, content: string): Observable<ChatMessageEntity> {
    return this.http
      .put<
        ResBodyType<ChatMessageEntity>
      >(`${this.baseUrl}/${chatId}/message/${messageId}`, { content }, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  deleteMessage(chatId: number, messageId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatId}/message/${messageId}`, {
      headers: this.headers,
    })
  }

  uploadAttachment(chatId: number, file: File, content?: string): Observable<ChatMessageEntity> {
    const formData = new FormData()
    formData.append('file', file)
    if (content) {
      formData.append('content', content)
    }

    return this.http
      .post<
        ResBodyType<ChatMessageEntity>
      >(`${this.baseUrl}/${chatId}/upload`, formData, { headers: this.headers })
      .pipe(map((res) => res.data))
  }

  addParticipant(chatId: number, userId: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/${chatId}/participant`,
      { userId },
      { headers: this.headers }
    )
  }

  removeParticipant(chatId: number, userId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${chatId}/participant/${userId}`, {
      headers: this.headers,
    })
  }

  markAsRead(chatId: number, messageId: number): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/${chatId}/read`,
      { messageId },
      { headers: this.headers }
    )
  }

  findActiveUsers(search: string, limit = 20): Observable<UserSearchResult[]> {
    return this.http
      .get<ResBodyType<UserSearchResult[]>>(`${this.baseUrl}/users`, {
        headers: this.headers,
        params: { search, limit: String(limit) },
      })
      .pipe(map((res) => res.data))
  }

  findOnlineUsers(): Observable<number[]> {
    return this.http
      .get<ResBodyType<number[]>>(`${this.baseUrl}/online`, {
        headers: this.headers,
      })
      .pipe(map((res) => res.data))
  }

  findGiphyMedia(
    type: GiphyMediaType,
    query: string,
    offset = 0,
    limit = 24
  ): Observable<GiphySearchResult> {
    const params: Record<string, string> = {
      limit: String(limit),
      offset: String(offset),
    }
    if (query.trim().length > 0) {
      params['query'] = query.trim()
    }

    return this.http
      .get<ResBodyType<GiphySearchResult>>(`${this.baseUrl}/giphy/${type}`, {
        headers: this.headers,
        params,
      })
      .pipe(map((res) => res.data))
  }
}
